import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  WorkInstruction, 
  Document, 
  WorkInstructionStatistics 
} from '../../services/workInstructionsApi';

// State interface
export interface WorkInstructionsState {
  // Work Instructions management
  workInstructions: {
    items: WorkInstruction[];
    selectedWorkInstruction: WorkInstruction | null;
    loading: boolean;
    error: string | null;
    validation: Record<string, string>;
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
    filters: {
      search: string;
      active?: boolean;
      hasDocument?: boolean;
    };
    statistics: WorkInstructionStatistics | null;
    bulkSelection: string[];
  };

  // Document management
  documents: {
    uploading: boolean;
    uploadProgress: number;
    uploadError: string | null;
    downloadError: string | null;
  };

  // UI states
  ui: {
    workInstructionDialogOpen: boolean;
    documentDialogOpen: boolean;
    documentPreviewOpen: boolean;
    selectedDocument: Document | null;
    bulkActionsDialogOpen: boolean;
  };
}

const initialState: WorkInstructionsState = {
  workInstructions: {
    items: [],
    selectedWorkInstruction: null,
    loading: false,
    error: null,
    validation: {},
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      search: '',
      active: undefined,
      hasDocument: undefined,
    },
    statistics: null,
    bulkSelection: [],
  },
  documents: {
    uploading: false,
    uploadProgress: 0,
    uploadError: null,
    downloadError: null,
  },
  ui: {
    workInstructionDialogOpen: false,
    documentDialogOpen: false,
    documentPreviewOpen: false,
    selectedDocument: null,
    bulkActionsDialogOpen: false,
  },
};

const workInstructionsSlice = createSlice({
  name: 'workInstructions',
  initialState,
  reducers: {
    // Work Instructions actions
    fetchWorkInstructionsStart: (state) => {
      state.workInstructions.loading = true;
      state.workInstructions.error = null;
    },
    fetchWorkInstructionsSuccess: (state, action: PayloadAction<{ workInstructions: WorkInstruction[]; total: number; page: number }>) => {
      state.workInstructions.loading = false;
      state.workInstructions.items = action.payload.workInstructions;
      state.workInstructions.pagination.total = action.payload.total;
      state.workInstructions.pagination.page = action.payload.page;
    },
    fetchWorkInstructionsFailure: (state, action: PayloadAction<string>) => {
      state.workInstructions.loading = false;
      state.workInstructions.error = action.payload;
    },

    setSelectedWorkInstruction: (state, action: PayloadAction<WorkInstruction | null>) => {
      state.workInstructions.selectedWorkInstruction = action.payload;
      state.workInstructions.validation = {};
    },

    setWorkInstructionValidation: (state, action: PayloadAction<Record<string, string>>) => {
      state.workInstructions.validation = action.payload;
    },

    setWorkInstructionFilters: (state, action: PayloadAction<Partial<WorkInstructionsState['workInstructions']['filters']>>) => {
      state.workInstructions.filters = { ...state.workInstructions.filters, ...action.payload };
      state.workInstructions.pagination.page = 1; // Reset to first page when filters change
    },

    setWorkInstructionPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.workInstructions.pagination.page = action.payload.page;
      state.workInstructions.pagination.limit = action.payload.limit;
    },

    setWorkInstructionBulkSelection: (state, action: PayloadAction<string[]>) => {
      state.workInstructions.bulkSelection = action.payload;
    },

    setWorkInstructionStatistics: (state, action: PayloadAction<WorkInstructionStatistics>) => {
      state.workInstructions.statistics = action.payload;
    },

    // Document actions
    setDocumentUploadStart: (state) => {
      state.documents.uploading = true;
      state.documents.uploadProgress = 0;
      state.documents.uploadError = null;
    },
    setDocumentUploadProgress: (state, action: PayloadAction<number>) => {
      state.documents.uploadProgress = action.payload;
    },
    setDocumentUploadSuccess: (state, action: PayloadAction<Document>) => {
      state.documents.uploading = false;
      state.documents.uploadProgress = 100;
      state.documents.uploadError = null;
      
      // Update selected work instruction with new document
      if (state.workInstructions.selectedWorkInstruction) {
        state.workInstructions.selectedWorkInstruction.Document = action.payload;
        state.workInstructions.selectedWorkInstruction.DocId = action.payload.Id;
      }
    },
    setDocumentUploadFailure: (state, action: PayloadAction<string>) => {
      state.documents.uploading = false;
      state.documents.uploadError = action.payload;
    },

    setDocumentDownloadError: (state, action: PayloadAction<string>) => {
      state.documents.downloadError = action.payload;
    },

    clearDocumentErrors: (state) => {
      state.documents.uploadError = null;
      state.documents.downloadError = null;
    },

    // UI actions
    setWorkInstructionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.workInstructionDialogOpen = action.payload;
      if (!action.payload) {
        state.workInstructions.selectedWorkInstruction = null;
        state.workInstructions.validation = {};
        state.documents.uploadError = null;
        state.documents.uploadProgress = 0;
      }
    },

    setDocumentDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.documentDialogOpen = action.payload;
      if (!action.payload) {
        state.documents.uploadError = null;
        state.documents.uploadProgress = 0;
      }
    },

    setDocumentPreviewOpen: (state, action: PayloadAction<{ open: boolean; document?: Document }>) => {
      state.ui.documentPreviewOpen = action.payload.open;
      state.ui.selectedDocument = action.payload.document || null;
    },

    setBulkActionsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.bulkActionsDialogOpen = action.payload;
    },

    // Clear errors
    clearWorkInstructionError: (state) => {
      state.workInstructions.error = null;
    },

    // Reset state
    resetWorkInstructionState: (state) => {
      state.workInstructions = initialState.workInstructions;
    },
    resetWorkInstructionsState: () => initialState,
  },
});

export const {
  // Work Instructions actions
  fetchWorkInstructionsStart,
  fetchWorkInstructionsSuccess,
  fetchWorkInstructionsFailure,
  setSelectedWorkInstruction,
  setWorkInstructionValidation,
  setWorkInstructionFilters,
  setWorkInstructionPagination,
  setWorkInstructionBulkSelection,
  setWorkInstructionStatistics,

  // Document actions
  setDocumentUploadStart,
  setDocumentUploadProgress,
  setDocumentUploadSuccess,
  setDocumentUploadFailure,
  setDocumentDownloadError,
  clearDocumentErrors,

  // UI actions
  setWorkInstructionDialogOpen,
  setDocumentDialogOpen,
  setDocumentPreviewOpen,
  setBulkActionsDialogOpen,

  // Clear errors
  clearWorkInstructionError,

  // Reset state
  resetWorkInstructionState,
  resetWorkInstructionsState,
} = workInstructionsSlice.actions;

export const workInstructionsReducer = workInstructionsSlice.reducer; 